import { useCallback, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Button, Grid2, Paper, Stack, Typography } from '@mui/material';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  FormField,
  FormFieldType,
  API_FIELDS_APPLICATION as APPLICATION,
  API_FIELDS_APPLICATION_PET as APPLICATION_PET,
} from '@enroller/admissions-components';

import { TitledSection } from '../../../layout/TitledSection';
import { yesNoOptions } from '../../../constants/yesNoOptions';
import { HousingFormValues, HOUSING_PAGE_SECTIONS, defaultValues } from './HousingFormValues';

const relativeSizeOfAnimalOptions = [
  { label: 'Small', value: 'Small' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Large', value: 'Large' },
];

const typeOfAnimalOptions = [
  { label: 'Dog', value: 'Dog' },
  { label: 'Cat', value: 'Cat' },
  { label: 'Bird', value: 'Bird' },
  { label: 'Fish', value: 'Fish' },
  { label: 'Other', value: 'Other' },
];

export const PetsSection = () => {
  const { control, clearErrors, getValues, reset } = useFormContext<HousingFormValues>();

  const section = HOUSING_PAGE_SECTIONS.pets;
  const doNotOwnPetsPath = `${section}.${APPLICATION.doNotOwnPets}`;

  const [isPetsOwnedDisabled, watchPetsIndoor, watchPetFear] = useWatch({
    control,
    name: [`${section}.${APPLICATION.doNotOwnPets}`, `${section}.${APPLICATION.adjustToIndoorPet}.value`, `${section}.${APPLICATION.afraidOfPets}.value`],
  });

  useEffect(() => {
    if (isPetsOwnedDisabled) {
      const currentSate = getValues();

      reset({
        ...currentSate,
        [HOUSING_PAGE_SECTIONS.pets]: {
          ...defaultValues[HOUSING_PAGE_SECTIONS.pets],
          [APPLICATION.doNotOwnPets]: true,
        },
      });

      clearErrors(HOUSING_PAGE_SECTIONS.pets);
    }
  }, [isPetsOwnedDisabled, clearErrors, getValues, reset]);

  return (
    <TitledSection title="Pets">
      <Grid2 size={12} spacing={0} container>
        <Grid2 size={12}>
          <Stack gap={3}>
            <Typography>What kind of pets do you have?</Typography>
            <FormField control={control} name={doNotOwnPetsPath} type={FormFieldType.Checkbox} label="I do not own any pets" />
          </Stack>
        </Grid2>

        <PetsList isPetsOwnedDisabled={isPetsOwnedDisabled} />
      </Grid2>

      <Grid2 container spacing={3} size={12}>
        <Grid2 size={12}>
          <Grid2 size={6}>
            <FormField
              control={control}
              type={FormFieldType.Radio}
              options={yesNoOptions}
              disabled={isPetsOwnedDisabled}
              label="Can you adjust to a home with an indoor pet?"
              name={`${section}.${APPLICATION.adjustToIndoorPet}.value`}
            />

            <FormField
              rows={6}
              multiline
              control={control}
              type={FormFieldType.Text}
              label="If no, please explain"
              disabled={watchPetsIndoor !== 'no' || isPetsOwnedDisabled}
              name={`${section}.${APPLICATION.adjustToIndoorPet}.other`}
            />
          </Grid2>
        </Grid2>

        <Grid2 size={12}>
          <Grid2 size={6}>
            <FormField
              control={control}
              type={FormFieldType.Radio}
              options={yesNoOptions}
              disabled={isPetsOwnedDisabled}
              label="Are you afraid of any pets?"
              name={`${section}.${APPLICATION.afraidOfPets}.value`}
            />

            <FormField
              rows={6}
              multiline
              control={control}
              type={FormFieldType.Text}
              label="If yes, please explain"
              disabled={watchPetFear !== 'yes' || isPetsOwnedDisabled}
              name={`${section}.${APPLICATION.afraidOfPets}.other`}
            />
          </Grid2>
        </Grid2>
      </Grid2>
    </TitledSection>
  );
};

const PetsList = ({ isPetsOwnedDisabled }: { isPetsOwnedDisabled: boolean }) => {
  const { control } = useFormContext<HousingFormValues>();

  const section = HOUSING_PAGE_SECTIONS.pets;
  const petListPath = `${section}.${APPLICATION.petsListGET}`;

  const { fields, append, remove } = useFieldArray({
    control,
    name: petListPath,
  });

  const handleAddPet = useCallback(() => {
    append({
      [APPLICATION_PET.name]: '',
      [APPLICATION_PET.type]: '',
      [APPLICATION_PET.size]: '',
    });
  }, [append]);

  return (
    <>
      <Grid2 spacing={3} container size={12} mb={2}>
        {fields.map((field, index) => (
          <Grid2 key={field.id} size={12}>
            <Paper sx={{ p: 2 }}>
              <Stack rowGap={3}>
                <Grid2 container columnSpacing={4}>
                  <Grid2 size={4}>
                    <FormField
                      control={control}
                      disabled={isPetsOwnedDisabled}
                      type={FormFieldType.Text}
                      label="Pet's Name"
                      name={`${petListPath}.${index}.${APPLICATION_PET.name}`}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <FormField
                      control={control}
                      disabled={isPetsOwnedDisabled}
                      type={FormFieldType.Dropdown}
                      label="Type of Animal"
                      options={typeOfAnimalOptions}
                      name={`${petListPath}.${index}.${APPLICATION_PET.type}`}
                    />
                  </Grid2>

                  <Grid2 size={4}>
                    <FormField
                      control={control}
                      type={FormFieldType.Radio}
                      disabled={isPetsOwnedDisabled}
                      label="Relative Size of Animal:"
                      options={relativeSizeOfAnimalOptions}
                      name={`${petListPath}.${index}.${APPLICATION_PET.size}`}
                    />
                  </Grid2>
                </Grid2>
                <Grid2 size={12}>
                  <Stack alignItems="start">
                    <Button disabled={isPetsOwnedDisabled} color="error" variant="outlined" onClick={() => remove(index)}>
                      Delete
                    </Button>
                  </Stack>
                </Grid2>
              </Stack>
            </Paper>
          </Grid2>
        ))}
      </Grid2>

      <Grid2 size={12}>
        <Button disabled={isPetsOwnedDisabled} startIcon={<AddIcon />} variant="outlined" color="primary" onClick={handleAddPet}>
          Add another Pet
        </Button>
      </Grid2>
    </>
  );
};
